import React, { useState } from 'react';
import { Edit3, X, Plus } from 'lucide-react';
import {
  useGetVehicleNotes,
  useAddVehicleNote,
  useUpdateVehicleNote,
  useDeleteVehicleNote,
} from '@/hooks/useVehicleNotes';
import { useAuth0 } from '@auth0/auth0-react';

// A member's name; renaming them renames their Driver rows too.
const getDisplayName = (user: any) => user.name;

interface VehicleNotesSectionProps {
  vehicleId: string | undefined;
  onError?: (error: string) => void;
}

const VehicleNotesSection: React.FC<VehicleNotesSectionProps> = ({
  vehicleId,
  onError,
}) => {
  const { user } = useAuth0();

  // A note's author edits or deletes it. This also let "admins" in by a
  // role the signed-in user's profile never carries, so it never did.
  const canModifyNote = (noteUser: any) => {
    if (!user) return false;
    return noteUser.sub === user.sub;
  };
  // VehicleNotes hooks
  const {
    data: vehicleNotesData,
    isLoading: isNotesLoading,
    error: notesError,
  } = useGetVehicleNotes(vehicleId);

  // Handle notes loading error
  React.useEffect(() => {
    if (notesError) {
      onError?.('Failed to load notes');
    }
  }, [notesError, onError]);
  const addVehicleNoteMutation = useAddVehicleNote();
  const updateVehicleNoteMutation = useUpdateVehicleNote();
  const deleteVehicleNoteMutation = useDeleteVehicleNote();

  // VehicleNotes state
  const [newNoteText, setNewNoteText] = useState('');
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteText, setEditingNoteText] = useState('');

  // VehicleNotes handlers
  const handleAddNote = async () => {
    if (!vehicleId || !newNoteText.trim()) return;

    try {
      await addVehicleNoteMutation.mutateAsync({
        vehicleId,
        noteData: { noteText: newNoteText.trim() },
      });
      setNewNoteText('');
    } catch (error: any) {
      console.error('Failed to add note:', error);
      onError?.(error.message || 'Failed to add note');
    }
  };

  const handleEditNote = (noteId: string, currentText: string) => {
    setEditingNoteId(noteId);
    setEditingNoteText(currentText);
  };

  const handleSaveEdit = async () => {
    if (!editingNoteId || !editingNoteText.trim()) return;

    try {
      await updateVehicleNoteMutation.mutateAsync({
        noteId: editingNoteId,
        noteData: { noteText: editingNoteText.trim() },
      });
      setEditingNoteId(null);
      setEditingNoteText('');
    } catch (error: any) {
      console.error('Failed to update note:', error);
      onError?.(error.message || 'Failed to update note');
    }
  };

  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditingNoteText('');
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!confirm('Are you sure you want to delete this note?')) return;

    try {
      await deleteVehicleNoteMutation.mutateAsync(noteId);
    } catch (error: any) {
      console.error('Failed to delete note:', error);
      onError?.(error.message || 'Failed to delete note');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Notes</h3>
        <span className="text-sm text-gray-500">
          {vehicleNotesData?.total || 0} notes
        </span>
      </div>

      {/* Existing Notes */}
      <div className="space-y-3">
        {isNotesLoading ? (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            <span className="ml-2 text-sm text-gray-600">Loading notes...</span>
          </div>
        ) : vehicleNotesData?.notes && vehicleNotesData.notes.length > 0 ? (
          vehicleNotesData.notes
            .sort(
              (a, b) =>
                new Date(a.createTime).getTime() -
                new Date(b.createTime).getTime()
            )
            .map((note) => (
              <div
                key={note.id}
                className="bg-gray-50 rounded-lg p-3 border border-gray-200"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      {getDisplayName(note.user)}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(note.createTime).toLocaleDateString()} at{' '}
                      {new Date(note.createTime).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                  {canModifyNote(note.user) && (
                    <div className="flex items-center gap-1 ml-2">
                      <button
                        onClick={() => handleEditNote(note.id, note.noteText)}
                        className="p-1 text-gray-400 hover:text-primary transition-colors"
                        disabled={editingNoteId === note.id}
                      >
                        <Edit3 size={14} />
                      </button>
                      <button
                        onClick={() => handleDeleteNote(note.id)}
                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                        disabled={deleteVehicleNoteMutation.isPending}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )}
                </div>

                {editingNoteId === note.id ? (
                  <div className="space-y-2">
                    <textarea
                      value={editingNoteText}
                      onChange={(e) => setEditingNoteText(e.target.value)}
                      className="w-full p-2 text-sm border border-gray-300 rounded resize-none focus:ring-2 focus:ring-primary focus:border-primary text-gray-900 bg-background"
                      rows={3}
                    />
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleSaveEdit}
                        disabled={
                          updateVehicleNoteMutation.isPending ||
                          !editingNoteText.trim()
                        }
                        className="px-3 py-1 text-sm bg-primary text-primary-foreground rounded hover:bg-primary/90 transition-colors disabled:opacity-50"
                      >
                        {updateVehicleNoteMutation.isPending
                          ? 'Saving...'
                          : 'Save'}
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="px-3 py-1 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">
                    {note.noteText}
                  </p>
                )}
              </div>
            ))
        ) : (
          <div className="text-center py-4 text-gray-500">
            <p className="text-sm">No notes yet for this vehicle</p>
          </div>
        )}
      </div>

      {/* Add New Note */}
      <div className="space-y-3">
        <textarea
          value={newNoteText}
          onChange={(e) => setNewNoteText(e.target.value)}
          placeholder="Add a note about this vehicle..."
          className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-primary focus:border-primary text-gray-900 bg-background placeholder-gray-400"
          rows={3}
        />
        <button
          onClick={handleAddNote}
          disabled={addVehicleNoteMutation.isPending || !newNoteText.trim()}
          className="w-full bg-primary text-primary-foreground py-2 px-4 rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {addVehicleNoteMutation.isPending ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Adding Note...
            </>
          ) : (
            <>
              <Plus size={16} />
              Add Note
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default VehicleNotesSection;
